import { createFileRoute } from '@tanstack/react-router';

import { dashboardT } from '../lib/messages';
import {
  resolveOwnedDashboardContext,
  ShigoDashboardContext,
} from '../features/auth/dashboardDestination';
import { useShigoSession } from '../lib/useShigoSession';
import { ShigoPermission } from '../features/auth/permissions';
import { useShigoPermissions } from '../features/auth/useShigoPermissions';
import { SavedReportPresetSelect } from '../features/reporting/savedReports/components/SavedReportPresetSelect';
import { ReportExplorerPage } from '../features/reporting/reportExplorer/components/ReportExplorerPage';
import {
  parseReportExplorerRouteSearch,
  reportExplorerRouteSearchSchema,
} from '../features/reporting/reportExplorer/reportExplorerRouteSearch';
import { useSavedReportQuery } from '../features/reporting/savedReports/hooks';
import { SavedReportSource } from '../features/reporting/savedReports/model';
import { savedReportToExplorerConfiguration } from '../features/reporting/savedReports/savedReportConfiguration';
import { useReportShareQuery } from '../features/reporting/reportExplorer/hooks';
import { reportRequestsToExplorerConfiguration } from '../features/reporting/reportExplorer/reportConfiguration';

export const Route = createFileRoute('/reporting/report-explorer')({
  validateSearch: reportExplorerRouteSearchSchema,
  component: ReportExplorerRoute,
});

function ReportExplorerRoute() {
  const search = Route.useSearch();
  const { session } = useShigoSession();
  const { can } = useShigoPermissions();
  const mainDashboard =
    session && resolveOwnedDashboardContext(session.user) === ShigoDashboardContext.Main;
  const savedReportQuery = useSavedReportQuery(
    mainDashboard && !search.u ? search.savedReportId : undefined,
    mainDashboard && !search.u ? search.savedReportSource : undefined,
  );
  const sharedReportQuery = useReportShareQuery(search.u);
  const requestedSharedReport = Boolean(search.u);
  const requestedSavedReport = Boolean(search.savedReportId && search.savedReportSource);

  if (!requestedSharedReport && requestedSavedReport && !mainDashboard) {
    return <ReportExplorerRouteState message={dashboardT('savedReports.runError')} />;
  }
  if (requestedSharedReport && sharedReportQuery.isLoading) {
    return <ReportExplorerRouteState message={dashboardT('reportExplorer.share.loading')} />;
  }
  if (
    requestedSharedReport &&
    (sharedReportQuery.isError ||
      !sharedReportQuery.data ||
      sharedReportQuery.data.publisherId !== null)
  ) {
    return <ReportExplorerRouteState message={dashboardT('reportExplorer.share.openError')} />;
  }
  if (!requestedSharedReport && requestedSavedReport && savedReportQuery.isLoading) {
    return <ReportExplorerRouteState message={dashboardT('savedReports.runLoading')} />;
  }
  if (
    !requestedSharedReport &&
    requestedSavedReport &&
    (savedReportQuery.isError || !savedReportQuery.data)
  ) {
    return <ReportExplorerRouteState message={dashboardT('savedReports.runError')} />;
  }

  const savedReport = savedReportQuery.data;
  const sharedReport = sharedReportQuery.data;
  const pageKey = sharedReport
    ? `share-${sharedReport.urlHash}`
    : `${search.savedReportSource ?? 'new'}-${search.savedReportId ?? 'report'}`;
  let initialConfiguration = parseReportExplorerRouteSearch(search);
  if (savedReport) initialConfiguration = savedReportToExplorerConfiguration(savedReport);
  if (sharedReport) {
    initialConfiguration = reportRequestsToExplorerConfiguration(
      sharedReport.report,
      sharedReport.chartReports,
      { ...sharedReport.settings, resolveRelativeDateRange: true },
    );
  }
  return (
    <ReportExplorerPage
      key={pageKey}
      initialConfiguration={initialConfiguration}
      initialSavedReport={
        !sharedReport && search.savedReportSource === SavedReportSource.Owned
          ? savedReport
          : undefined
      }
      savedReportPicker={
        mainDashboard && can(ShigoPermission.ReportingSavedReportsRead) ? (
          <SavedReportPresetSelect
            savedReportId={sharedReport ? undefined : search.savedReportId}
            savedReportSource={sharedReport ? undefined : search.savedReportSource}
          />
        ) : null
      }
    />
  );
}

function ReportExplorerRouteState({ message }: { message: string }) {
  return (
    <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
      <div className="border-border bg-card text-muted-foreground mx-auto w-full max-w-[1920px] rounded-xl border p-8 text-sm">
        {message}
      </div>
    </main>
  );
}
